import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ForceGraph2D from 'react-force-graph-2d';
import { Share2, RefreshCw, Zap, ZoomIn, ZoomOut, X, Maximize2 } from 'lucide-react';
import { API_BASE_URL as BASE_URL } from '../config';

interface NodeData {
  id: string;
  label: string;
  type: 'OriginalPost' | 'Author' | 'Bot' | 'NewsSource' | 'Channel' | 'FactChecker' | 'RedditPost' | 'VideoPost';
  platform: string;
  riskLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  followers: number;
  engagement: number;
  detail: string;
  val?: number;
}

interface LinkData { source: string; target: string; value?: number; }
interface GraphData { nodes: NodeData[]; links: LinkData[]; }

const SAMPLE_GRAPH: GraphData = {
  nodes: [
    { id: 'post_root', label: 'Original Tweet', type: 'OriginalPost', platform: 'Twitter/X', riskLevel: 'CRITICAL', followers: 0, engagement: 14200, detail: 'Fabricated election fraud claim — origin tweet', val: 8 },
    { id: 'u_author',  label: '@PoliticsNow2024', type: 'Author', platform: 'Twitter/X', riskLevel: 'HIGH', followers: 52000, engagement: 8900, detail: 'Account with history of spreading election misinformation', val: 5 },
    { id: 'u_bot1',   label: '@AutoAmplify_1', type: 'Bot', platform: 'Twitter/X', riskLevel: 'CRITICAL', followers: 120, engagement: 340, detail: 'Bot account — 99% retweet activity, created 3 weeks ago', val: 4 },
    { id: 'u_bot2',   label: '@AutoAmplify_2', type: 'Bot', platform: 'Twitter/X', riskLevel: 'CRITICAL', followers: 88, engagement: 290, detail: 'Bot cluster member — coordinated amplification detected', val: 4 },
    { id: 'u_news',   label: 'GossipBreaking.net', type: 'NewsSource', platform: 'Web', riskLevel: 'HIGH', followers: 310000, engagement: 22400, detail: 'Low-credibility news site — republished claim without verification', val: 6 },
    { id: 'u_telegram', label: 'TG: TruthOrders', type: 'Channel', platform: 'Telegram', riskLevel: 'HIGH', followers: 41000, engagement: 9100, detail: 'Telegram channel with 41K subscribers sharing unverified claims', val: 5 },
    { id: 'u_fc',     label: 'FactCheckHQ', type: 'FactChecker', platform: 'Web', riskLevel: 'LOW', followers: 89000, engagement: 3200, detail: 'Debunked claim — rated FALSE. Original source does not exist', val: 5 },
    { id: 'u_reddit', label: 'r/conspiracy post', type: 'RedditPost', platform: 'Reddit', riskLevel: 'MEDIUM', followers: 0, engagement: 1870, detail: 'Reddit thread with 1.8K upvotes linking to the original tweet', val: 4 },
    { id: 'u_yt',    label: 'YT: "Election Fraud EXPOSED"', type: 'VideoPost', platform: 'YouTube', riskLevel: 'HIGH', followers: 124000, engagement: 56000, detail: 'YouTube video with 56K views using tweet as "evidence"', val: 6 },
  ],
  links: [
    { source: 'u_author',   target: 'post_root',   value: 3 },
    { source: 'post_root',  target: 'u_bot1',      value: 2 },
    { source: 'post_root',  target: 'u_bot2',      value: 2 },
    { source: 'post_root',  target: 'u_news',      value: 3 },
    { source: 'u_news',     target: 'u_telegram',  value: 2 },
    { source: 'post_root',  target: 'u_reddit',    value: 2 },
    { source: 'u_reddit',   target: 'u_yt',        value: 2 },
    { source: 'u_fc',       target: 'post_root',   value: 1 },
    { source: 'u_telegram', target: 'u_bot1',      value: 1 },
  ],
};

const NODE_COLORS: Record<string, string> = {
  OriginalPost:  '#f43f5e',
  Author:        '#f59e0b',
  Bot:           '#ef4444',
  NewsSource:    '#8b5cf6',
  Channel:       '#06b6d4',
  FactChecker:   '#22c55e',
  RedditPost:    '#ff6314',
  VideoPost:     '#3b82f6',
};

const RISK_COLORS: Record<string, string> = {
  CRITICAL: '#f43f5e', HIGH: '#f59e0b', MEDIUM: '#06b6d4', LOW: '#22c55e',
};

/** Transform the backend /analytics/network-graph response into ForceGraph2D format */
function transformApiGraph(data: { nodes: any[]; edges: any[] }): GraphData {
  const groupToType = (group: string, risk: number): NodeData['type'] => {
    if (group === 'center')    return 'OriginalPost';
    if (group === 'platform')  return 'Channel';
    if (group === 'author')    return risk > 0.7 ? 'Bot' : 'Author';
    if (group === 'topic')     return 'NewsSource';
    return 'Author';
  };
  const riskToLevel = (risk: number): NodeData['riskLevel'] =>
    risk > 0.7 ? 'CRITICAL' : risk > 0.5 ? 'HIGH' : risk > 0.3 ? 'MEDIUM' : 'LOW';

  const platformFromId = (id: string, label: string): string => {
    if (id.startsWith('platform_')) return label;
    if (id.includes('twitter'))    return 'Twitter/X';
    if (id.includes('reddit'))     return 'Reddit';
    if (id.includes('youtube'))    return 'YouTube';
    if (id.includes('news'))       return 'Web';
    return '';
  };

  const nodes: NodeData[] = (data.nodes || []).map((n: any) => {
    const risk = typeof n.risk === 'number' ? n.risk : 0.3;
    return {
      id:        n.id,
      label:     n.label || n.id,
      type:      groupToType(n.group || 'author', risk),
      platform:  platformFromId(n.id, n.label || ''),
      riskLevel: riskToLevel(risk),
      followers: 0,
      engagement: 0,
      detail:    `${n.group || 'node'} — OSINT risk: ${(risk * 100).toFixed(0)}%`,
      val:       n.size ? Math.max(2, Math.round(n.size / 4)) : 4,
    };
  });

  const links: LinkData[] = (data.edges || []).map((e: any) => ({
    source: e.from,
    target: e.to,
    value:  e.value || 1,
  }));

  return { nodes, links };
}

export const PropagationGraph: React.FC = () => {
  const GRAPH_HEIGHT = 430;

  const fgRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [graphWidth, setGraphWidth] = useState(600);
  const [hoverNode, setHoverNode] = useState<NodeData | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [graphData, setGraphData] = useState<GraphData>(SAMPLE_GRAPH);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<NodeData | null>(null);
  const [expanded, setExpanded] = useState(false);
  const fgExpandedRef = useRef<any>(null);
  const expandedContainerRef = useRef<HTMLDivElement>(null);
  const [expandDims, setExpandDims] = useState({ w: 1000, h: 600 });

  /* Fetch real OSINT network graph from the 6-layer analysis pipeline */
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE_URL}/api/analytics/network-graph?limit=120`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: { nodes: any[]; edges: any[]; stats?: any }) => {
        if (data?.nodes?.length > 2) {
          setGraphData(transformApiGraph(data));
          setIsLive(true);
        }
      })
      .catch(() => { /* keep SAMPLE_GRAPH fallback */ })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setGraphWidth(containerRef.current.offsetWidth);
      }
    };
    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) observer.observe(containerRef.current);
    updateWidth();
    return () => observer.disconnect();
  }, []);

  /* Escape key closes expanded overlay */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Measure expanded container size + reheat simulation when overlay opens */
  useEffect(() => {
    if (!expanded) return;
    const update = () => {
      if (expandedContainerRef.current) {
        const r = expandedContainerRef.current.getBoundingClientRect();
        setExpandDims({ w: Math.floor(r.width), h: Math.floor(r.height) });
      }
    };
    const t = setTimeout(update, 50);
    const obs = new ResizeObserver(update);
    if (expandedContainerRef.current) obs.observe(expandedContainerRef.current);
    return () => { clearTimeout(t); obs.disconnect(); };
  }, [expanded]);

  /* Sync force params in expanded graph when data changes */
  useEffect(() => {
    if (!expanded || !fgExpandedRef.current || graphData.nodes.length === 0) return;
    const n = graphData.nodes.length;
    try {
      fgExpandedRef.current.d3Force('charge')?.strength(Math.max(-400, -(60 + n * 14)));
      fgExpandedRef.current.d3Force('link')?.distance(Math.min(180, 50 + n * 5));
      fgExpandedRef.current.d3ReheatSimulation();
    } catch (_) {}
  }, [expanded, graphData]);

  /* Spread nodes proportional to count so they fill the canvas */
  useEffect(() => {
    if (!fgRef.current || graphData.nodes.length === 0) return;
    const n = graphData.nodes.length;
    const chargeStrength = Math.max(-400, -(60 + n * 14));
    const linkDistance  = Math.min(180, 50 + n * 5);
    try {
      fgRef.current.d3Force('charge')?.strength(chargeStrength);
      fgRef.current.d3Force('link')?.distance(linkDistance);
      fgRef.current.d3ReheatSimulation();
    } catch (_) { /* not ready yet */ }
  }, [graphData]);

  const handleNodeHover = useCallback((node: NodeData | null) => {
    setHoverNode(node || null);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left + 14, y: e.clientY - rect.top - 10 });
    }
  }, []);

  const handleExpandedMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (expandedContainerRef.current) {
      const rect = expandedContainerRef.current.getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left + 14, y: e.clientY - rect.top - 10 });
    }
  }, []);

  const drawNode = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    // Guard: node coordinates may be NaN/Infinity during force-graph warmup ticks
    if (!isFinite(node.x) || !isFinite(node.y)) return;

    const r = (node.val || 4) * 1.5;
    const color = NODE_COLORS[node.type] || '#a1a1aa';
    const isHovered = hoverNode?.id === node.id;
    const isCritical = node.riskLevel === 'CRITICAL';

    // Time-based pulse for CRITICAL nodes (requires continuous render from particles)
    const t = Date.now() / 1000;
    const pulse = isCritical ? 0.5 + 0.5 * Math.sin(t * 2.5 + node.id.length) : 0;

    // Outer pulsing aura for CRITICAL
    if (isCritical) {
      const auraR = r + 5 + pulse * 7;
      const grad = ctx.createRadialGradient(node.x, node.y, r * 0.5, node.x, node.y, auraR + 6);
      grad.addColorStop(0, color + '55');
      grad.addColorStop(1, color + '00');
      ctx.beginPath();
      ctx.arc(node.x, node.y, auraR + 6, 0, 2 * Math.PI);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Hover outer ring
    if (isHovered) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 6, 0, 2 * Math.PI);
      ctx.fillStyle = color + '28';
      ctx.fill();
    }

    // Main node fill
    ctx.beginPath();
    ctx.arc(node.x, node.y, r + (isHovered ? 1.5 : 0), 0, 2 * Math.PI);
    ctx.fillStyle = isHovered ? color : color + 'cc';
    ctx.fill();

    // Node border
    ctx.strokeStyle = isHovered ? '#ffffff' : isCritical ? color : color + '80';
    ctx.lineWidth = isHovered ? 2.5 : isCritical ? 1.5 + pulse : 1;
    ctx.stroke();

    // Animated outer ring for CRITICAL (pulsing ring that expands and fades)
    if (isCritical) {
      const ringR = r + 3 + pulse * 9;
      const alpha = Math.floor((1 - pulse) * 180).toString(16).padStart(2, '0');
      ctx.beginPath();
      ctx.arc(node.x, node.y, ringR, 0, 2 * Math.PI);
      ctx.strokeStyle = color + alpha;
      ctx.lineWidth = 1.2;
      ctx.stroke();
    }

    // Labels — always visible, adaptive to zoom and node count
    const label = node.label as string;
    const fontSize = Math.max(7, Math.min(13, 11 / Math.max(0.35, globalScale)));
    const maxLen = globalScale > 1.4 ? 22 : globalScale > 0.7 ? 14 : 9;
    const displayLabel = label.length > maxLen ? label.slice(0, maxLen - 1) + '…' : label;
    ctx.font = `${isHovered ? '700 ' : '500 '}${fontSize}px Inter,system-ui,sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    // Text shadow for readability on dark canvas
    ctx.shadowColor = '#000000';
    ctx.shadowBlur = 5;
    ctx.fillStyle = isHovered ? '#ffffff' : '#cbd5e1';
    ctx.fillText(displayLabel, node.x, node.y + r + 3);
    ctx.shadowBlur = 0;
  }, [hoverNode]);

  // Derived data for the expanded analysis panel
  const riskOrder: Record<string, number> = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
  const typeCounts: Record<string, number> = Object.fromEntries(
    Object.keys(NODE_COLORS).map(t => [t, graphData.nodes.filter(n => n.type === t).length])
  );
  const riskCounts = {
    CRITICAL: graphData.nodes.filter(n => n.riskLevel === 'CRITICAL').length,
    HIGH:     graphData.nodes.filter(n => n.riskLevel === 'HIGH').length,
    MEDIUM:   graphData.nodes.filter(n => n.riskLevel === 'MEDIUM').length,
    LOW:      graphData.nodes.filter(n => n.riskLevel === 'LOW').length,
  };
  const topRiskNodes = [...graphData.nodes]
    .sort((a, b) => riskOrder[b.riskLevel] - riskOrder[a.riskLevel])
    .slice(0, 8);

  return (
    <div className="glass-panel rounded-2xl p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-violet-500/15 border border-violet-500/25">
            <Share2 className="w-3.5 h-3.5 text-violet-400" />
          </div>
          <span className="text-white font-bold text-sm tracking-wide">Node Propagation Tree</span>
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${isLive ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-zinc-700/40 border border-zinc-600/40 text-zinc-400'}`}>
            <Zap className="w-2.5 h-2.5" />
            {loading ? 'Connecting…' : isLive ? '● Live OSINT' : '◌ Demo Data'}
          </span>
          <span className="text-xs text-zinc-400 tabular-nums">{graphData.nodes.length} nodes</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => fgRef.current?.zoom(fgRef.current.zoom() * 1.3, 200)}
            className="text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-800" title="Zoom in">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => fgRef.current?.zoom(fgRef.current.zoom() / 1.3, 200)}
            className="text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-800" title="Zoom out">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { fgRef.current?.zoomToFit(400, 40); setSelectedNode(null); }}
            className="text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-800" title="Re-center">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setExpanded(true)}
            className="text-zinc-400 hover:text-violet-300 transition-colors p-1.5 rounded-lg hover:bg-violet-950/40 border border-transparent hover:border-violet-500/30"
            title="Expand to full screen">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Graph + side panel row */}
      <div className="flex gap-2" style={{ height: GRAPH_HEIGHT }}>
        <div ref={containerRef} className="relative rounded-xl overflow-hidden bg-zinc-900/60 border border-zinc-800/50 flex-1" onMouseMove={handleMouseMove}>
          <ForceGraph2D
            ref={fgRef}
            graphData={graphData}
            width={graphWidth}
            height={GRAPH_HEIGHT}
            backgroundColor="transparent"
            nodeCanvasObject={drawNode}
            nodeCanvasObjectMode={() => 'replace'}
            linkColor={() => '#52525b'}
            linkWidth={(link: any) => (link.value || 1) * 0.8}
            linkDirectionalArrowLength={4}
            linkDirectionalArrowRelPos={0.88}
            linkDirectionalArrowColor={() => '#71717a'}
            linkDirectionalParticles={2}
            linkDirectionalParticleSpeed={0.004}
            linkDirectionalParticleWidth={2}
            linkDirectionalParticleColor={(link: any) => {
              const srcId = typeof link.source === 'object' ? link.source.id : link.source;
              const srcNode = graphData.nodes.find(n => n.id === srcId);
              return srcNode ? (NODE_COLORS[srcNode.type] || '#8b5cf6') + 'cc' : '#8b5cf6cc';
            }}
            onNodeHover={handleNodeHover}
            onNodeClick={(node: any) => {
              setSelectedNode(prev => prev?.id === node.id ? null : (node as NodeData));
              fgRef.current?.centerAt(node.x, node.y, 500);
            }}
            nodeLabel={() => ''}
            cooldownTicks={120}
            warmupTicks={30}
            d3VelocityDecay={0.35}
            d3AlphaDecay={0.015}
            onEngineStop={() => fgRef.current?.zoomToFit(500, 40)}
          />

          {/* Hover tooltip */}
          {hoverNode && !selectedNode && (
            <div style={{
              position: 'absolute',
              left: Math.min(tooltipPos.x, graphWidth - 230),
              top: Math.max(tooltipPos.y, 8),
              pointerEvents: 'none',
              zIndex: 50,
            }}>
              <div style={{
                background: 'rgba(9,9,11,0.96)',
                border: `1px solid ${NODE_COLORS[hoverNode.type]}55`,
                borderRadius: 12,
                minWidth: 210,
                maxWidth: 250,
                overflow: 'hidden',
                boxShadow: `0 4px 24px rgba(0,0,0,0.7), 0 0 0 1px ${NODE_COLORS[hoverNode.type]}22`,
                backdropFilter: 'blur(12px)',
              }}>
                {/* Gradient header */}
                <div style={{
                  background: `linear-gradient(135deg, ${NODE_COLORS[hoverNode.type]}25, ${NODE_COLORS[hoverNode.type]}08)`,
                  borderBottom: `1px solid ${NODE_COLORS[hoverNode.type]}30`,
                  padding: '8px 12px',
                }}>
                  <div style={{ color: NODE_COLORS[hoverNode.type], fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{hoverNode.label}</div>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <span style={{ background: NODE_COLORS[hoverNode.type] + '25', color: NODE_COLORS[hoverNode.type], borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 600, letterSpacing: '0.04em' }}>{hoverNode.type}</span>
                    <span style={{ background: RISK_COLORS[hoverNode.riskLevel] + '25', color: RISK_COLORS[hoverNode.riskLevel], borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' }}>⚠ {hoverNode.riskLevel}</span>
                  </div>
                </div>
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 7, lineHeight: 1.5 }}>{hoverNode.detail}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px', fontSize: 10 }}>
                    {hoverNode.platform && <><span style={{ color: '#52525b' }}>Platform</span><span style={{ color: '#e2e8f0' }}>{hoverNode.platform}</span></>}
                    {hoverNode.followers > 0 && <><span style={{ color: '#52525b' }}>Followers</span><span style={{ color: '#e2e8f0' }}>{hoverNode.followers.toLocaleString()}</span></>}
                    {hoverNode.engagement > 0 && <><span style={{ color: '#52525b' }}>Engagement</span><span style={{ color: '#e2e8f0' }}>{hoverNode.engagement.toLocaleString()}</span></>}
                  </div>
                </div>
                <div style={{ padding: '5px 12px 7px', borderTop: `1px solid ${NODE_COLORS[hoverNode.type]}20`, color: '#71717a', fontSize: 9 }}>Click node to pin details</div>
              </div>
            </div>
          )}

          {/* Empty state hint */}
          {graphData.nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-xs">No graph data</div>
          )}
        </div>

        {/* Pinned node detail panel */}
        {selectedNode && (
          <div className="w-52 shrink-0 rounded-xl bg-zinc-900/80 border flex flex-col overflow-hidden" style={{ borderColor: NODE_COLORS[selectedNode.type] + '50' }}>
            {/* Panel header */}
            <div className="p-3 border-b flex items-start justify-between gap-2" style={{ background: `linear-gradient(135deg, ${NODE_COLORS[selectedNode.type]}20, transparent)`, borderColor: NODE_COLORS[selectedNode.type] + '30' }}>
              <div>
                <div className="text-[11px] font-bold leading-tight" style={{ color: NODE_COLORS[selectedNode.type] }}>{selectedNode.label}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: NODE_COLORS[selectedNode.type] + '25', color: NODE_COLORS[selectedNode.type] }}>{selectedNode.type}</span>
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: RISK_COLORS[selectedNode.riskLevel] + '25', color: RISK_COLORS[selectedNode.riskLevel] }}>⚠ {selectedNode.riskLevel}</span>
                </div>
              </div>
              <button onClick={() => setSelectedNode(null)} className="text-zinc-400 hover:text-zinc-300 transition-colors shrink-0 mt-0.5">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Risk bar */}
            <div className="px-3 pt-2.5 pb-0">
              <div className="text-xs text-zinc-400 uppercase tracking-widest mb-1">Risk Level</div>
              <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{
                  width: selectedNode.riskLevel === 'CRITICAL' ? '95%' : selectedNode.riskLevel === 'HIGH' ? '70%' : selectedNode.riskLevel === 'MEDIUM' ? '45%' : '20%',
                  background: RISK_COLORS[selectedNode.riskLevel],
                  boxShadow: `0 0 8px ${RISK_COLORS[selectedNode.riskLevel]}`,
                }} />
              </div>
            </div>
            {/* Details */}
            <div className="p-3 flex flex-col gap-2 flex-1 overflow-y-auto">
              <div className="text-xs text-zinc-400 leading-relaxed">{selectedNode.detail}</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {selectedNode.platform && <><span className="text-zinc-400">Platform</span><span className="text-zinc-200 truncate">{selectedNode.platform}</span></>}
                {selectedNode.followers > 0 && <><span className="text-zinc-400">Followers</span><span className="text-zinc-200">{selectedNode.followers.toLocaleString()}</span></>}
                {selectedNode.engagement > 0 && <><span className="text-zinc-400">Engagement</span><span className="text-zinc-200">{selectedNode.engagement.toLocaleString()}</span></>}
              </div>
            </div>
            {/* Node ID footer */}
            <div className="px-3 py-2 border-t border-zinc-800/60 text-xs text-zinc-400 font-mono truncate">ID: {selectedNode.id}</div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 pt-2 border-t border-zinc-800/50">
        {Object.entries(NODE_COLORS).map(([type, color]) => (
          <span key={type} className="flex items-center gap-1 text-xs text-zinc-400">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {type}
          </span>
        ))}
      </div>

      {/* ── GLASSMORPHISM FULLSCREEN EXPANDED OVERLAY ── */}
      {expanded && createPortal(
        <div className="fixed inset-0 z-[1500] flex">
          <div className="absolute inset-0 bg-zinc-950/78 backdrop-blur-[10px]" />
          <div className="relative z-10 flex w-full h-full p-3 gap-3">

            {/* LEFT: full-screen force graph */}
            <div className="flex-1 relative rounded-2xl overflow-hidden border border-violet-500/20 shadow-[0_0_80px_rgba(139,92,246,0.18)] bg-zinc-950/60">

              {/* Animated scan line */}
              <div className="osint-scan absolute left-0 right-0 h-px z-[350] pointer-events-none"
                style={{ background: 'linear-gradient(to right, transparent, #7c3aed60, #8b5cf6cc, #7c3aed60, transparent)' }} />

              {/* Header bar */}
              <div className="absolute top-0 left-0 right-0 z-[50] flex items-center justify-between px-4 py-2.5 bg-zinc-950/80 backdrop-blur border-b border-violet-500/15">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-violet-500/15 border border-violet-500/25">
                    <Share2 className="w-3.5 h-3.5 text-violet-400" />
                  </div>
                  <span className="text-white font-bold text-sm tracking-wide">Node Propagation Tree</span>
                  <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${isLive ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-zinc-700/40 border border-zinc-600/40 text-zinc-400'}`}>
                    <Zap className="w-2.5 h-2.5" />
                    {isLive ? '● Live OSINT' : '◌ Demo Data'}
                  </span>
                  <span className="text-xs text-zinc-400 tabular-nums">{graphData.nodes.length} nodes · {graphData.links.length} links</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => fgExpandedRef.current?.zoom(fgExpandedRef.current.zoom() * 1.3, 200)}
                    className="text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-800" title="Zoom in">
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => fgExpandedRef.current?.zoom(fgExpandedRef.current.zoom() / 1.3, 200)}
                    className="text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-800" title="Zoom out">
                    <ZoomOut className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => { fgExpandedRef.current?.zoomToFit(400, 40); setSelectedNode(null); }}
                    className="text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-800" title="Re-center">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <div className="w-px h-4 bg-zinc-700 mx-1" />
                  <button onClick={() => setExpanded(false)}
                    className="text-zinc-400 hover:text-rose-300 transition-colors p-1.5 rounded-lg hover:bg-rose-950/40"
                    title="Close (Esc)">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Graph canvas — fills space between header and legend footer */}
              <div ref={expandedContainerRef} className="absolute inset-0 top-[46px] bottom-[34px]" onMouseMove={handleExpandedMouseMove}>
                <ForceGraph2D
                  ref={fgExpandedRef}
                  graphData={graphData}
                  width={expandDims.w}
                  height={expandDims.h}
                  backgroundColor="transparent"
                  nodeCanvasObject={drawNode}
                  nodeCanvasObjectMode={() => 'replace'}
                  linkColor={() => '#52525b'}
                  linkWidth={(link: any) => (link.value || 1) * 0.8}
                  linkDirectionalArrowLength={5}
                  linkDirectionalArrowRelPos={0.88}
                  linkDirectionalArrowColor={() => '#71717a'}
                  linkDirectionalParticles={2}
                  linkDirectionalParticleSpeed={0.004}
                  linkDirectionalParticleWidth={2}
                  linkDirectionalParticleColor={(link: any) => {
                    const srcId = typeof link.source === 'object' ? link.source.id : link.source;
                    const srcNode = graphData.nodes.find(n => n.id === srcId);
                    return srcNode ? (NODE_COLORS[srcNode.type] || '#8b5cf6') + 'cc' : '#8b5cf6cc';
                  }}
                  onNodeHover={handleNodeHover}
                  onNodeClick={(node: any) => {
                    setSelectedNode(prev => prev?.id === node.id ? null : (node as NodeData));
                    fgExpandedRef.current?.centerAt(node.x, node.y, 500);
                  }}
                  nodeLabel={() => ''}
                  cooldownTicks={120}
                  warmupTicks={60}
                  d3VelocityDecay={0.35}
                  d3AlphaDecay={0.015}
                  onEngineStop={() => fgExpandedRef.current?.zoomToFit(500, 40)}
                />

                {/* Hover tooltip */}
                {hoverNode && !selectedNode && (
                  <div style={{ position: 'absolute', left: Math.min(tooltipPos.x, expandDims.w - 260), top: Math.max(tooltipPos.y, 8), pointerEvents: 'none', zIndex: 200 }}>
                    <div style={{ background: 'rgba(9,9,11,0.96)', border: `1px solid ${NODE_COLORS[hoverNode.type]}55`, borderRadius: 12, minWidth: 210, maxWidth: 260, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.7)', backdropFilter: 'blur(12px)' }}>
                      <div style={{ background: `linear-gradient(135deg, ${NODE_COLORS[hoverNode.type]}25, ${NODE_COLORS[hoverNode.type]}08)`, borderBottom: `1px solid ${NODE_COLORS[hoverNode.type]}30`, padding: '8px 12px' }}>
                        <div style={{ color: NODE_COLORS[hoverNode.type], fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{hoverNode.label}</div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <span style={{ background: NODE_COLORS[hoverNode.type] + '25', color: NODE_COLORS[hoverNode.type], borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>{hoverNode.type}</span>
                          <span style={{ background: RISK_COLORS[hoverNode.riskLevel] + '25', color: RISK_COLORS[hoverNode.riskLevel], borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>⚠ {hoverNode.riskLevel}</span>
                        </div>
                      </div>
                      <div style={{ padding: '8px 12px' }}>
                        <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 7, lineHeight: 1.5 }}>{hoverNode.detail}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px', fontSize: 10 }}>
                          {hoverNode.platform && <><span style={{ color: '#52525b' }}>Platform</span><span style={{ color: '#e2e8f0' }}>{hoverNode.platform}</span></>}
                          {hoverNode.followers > 0 && <><span style={{ color: '#52525b' }}>Followers</span><span style={{ color: '#e2e8f0' }}>{hoverNode.followers.toLocaleString()}</span></>}
                          {hoverNode.engagement > 0 && <><span style={{ color: '#52525b' }}>Engagement</span><span style={{ color: '#e2e8f0' }}>{hoverNode.engagement.toLocaleString()}</span></>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Legend footer */}
              <div className="absolute bottom-0 left-0 right-0 z-[50] flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 bg-zinc-950/70 backdrop-blur border-t border-zinc-800/50">
                {Object.entries(NODE_COLORS).map(([type, color]) => (
                  <span key={type} className="flex items-center gap-1 text-xs text-zinc-400">
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                    {type}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT: Analysis panel */}
            <div className="w-[300px] flex-shrink-0 flex flex-col gap-3 overflow-y-auto">

              {/* Network overview stats */}
              <div className="rounded-xl bg-zinc-900/80 border border-violet-500/20 p-3">
                <div className="text-xs font-bold text-violet-300 uppercase tracking-widest mb-2">Network Overview</div>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { label: 'Total Nodes', value: graphData.nodes.length, color: '#8b5cf6' },
                    { label: 'Total Links', value: graphData.links.length, color: '#06b6d4' },
                    { label: 'CRITICAL', value: riskCounts.CRITICAL, color: '#f43f5e' },
                    { label: 'HIGH Risk', value: riskCounts.HIGH, color: '#f59e0b' },
                  ] as const).map(s => (
                    <div key={s.label} className="rounded-lg bg-zinc-800/50 border border-zinc-700/30 px-2.5 py-2 text-center">
                      <div className="text-xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div>
                      <div className="text-xs text-zinc-400 uppercase tracking-wide mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Node type distribution */}
              <div className="rounded-xl bg-zinc-900/80 border border-zinc-700/30 p-3">
                <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">Node Type Distribution</div>
                <div className="flex flex-col gap-1.5">
                  {Object.entries(typeCounts).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                    <div key={type} className="flex items-center gap-2">
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: NODE_COLORS[type], display: 'inline-block', flexShrink: 0 }} />
                      <span className="text-xs text-zinc-400 w-[88px] truncate">{type}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${graphData.nodes.length > 0 ? (count / graphData.nodes.length) * 100 : 0}%`, background: NODE_COLORS[type], opacity: 0.85 }} />
                      </div>
                      <span className="text-xs font-bold tabular-nums w-3 text-right" style={{ color: NODE_COLORS[type] }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top risk nodes */}
              <div className="rounded-xl bg-zinc-900/80 border border-zinc-700/30 p-3 flex-1 min-h-0 overflow-y-auto">
                <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">Top Risk Nodes</div>
                <div className="flex flex-col gap-1.5">
                  {topRiskNodes.map((node, i) => (
                    <button key={node.id}
                      onClick={() => setSelectedNode(prev => prev?.id === node.id ? null : node)}
                      className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-left border transition-all ${
                        selectedNode?.id === node.id
                          ? 'border-violet-500/50 bg-violet-950/40'
                          : 'border-zinc-700/20 bg-zinc-800/30 hover:border-zinc-600/40 hover:bg-zinc-800/50'
                      }`}>
                      <span className="text-xs text-zinc-400 tabular-nums w-3 shrink-0">#{i + 1}</span>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: NODE_COLORS[node.type], display: 'inline-block', flexShrink: 0 }} />
                      <span className="text-xs text-zinc-200 flex-1 truncate">{node.label}</span>
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: RISK_COLORS[node.riskLevel] + '25', color: RISK_COLORS[node.riskLevel] }}>{node.riskLevel}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected node detail */}
              {selectedNode && (
                <div className="rounded-xl border overflow-hidden flex-shrink-0" style={{ borderColor: NODE_COLORS[selectedNode.type] + '50' }}>
                  <div className="p-3 border-b flex items-start justify-between gap-2" style={{ background: `linear-gradient(135deg, ${NODE_COLORS[selectedNode.type]}20, transparent)`, borderColor: NODE_COLORS[selectedNode.type] + '30' }}>
                    <div>
                      <div className="text-[11px] font-bold leading-tight" style={{ color: NODE_COLORS[selectedNode.type] }}>{selectedNode.label}</div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: NODE_COLORS[selectedNode.type] + '25', color: NODE_COLORS[selectedNode.type] }}>{selectedNode.type}</span>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: RISK_COLORS[selectedNode.riskLevel] + '25', color: RISK_COLORS[selectedNode.riskLevel] }}>⚠ {selectedNode.riskLevel}</span>
                      </div>
                    </div>
                    <button onClick={() => setSelectedNode(null)} className="text-zinc-400 hover:text-zinc-300 transition-colors shrink-0 mt-0.5">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="p-3 bg-zinc-900/80 text-xs text-zinc-400 leading-relaxed">{selectedNode.detail}</div>
                  {(selectedNode.platform || selectedNode.followers > 0 || selectedNode.engagement > 0) && (
                    <div className="px-3 pb-3 bg-zinc-900/80 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      {selectedNode.platform && <><span className="text-zinc-400">Platform</span><span className="text-zinc-200 truncate">{selectedNode.platform}</span></>}
                      {selectedNode.followers > 0 && <><span className="text-zinc-400">Followers</span><span className="text-zinc-200">{selectedNode.followers.toLocaleString()}</span></>}
                      {selectedNode.engagement > 0 && <><span className="text-zinc-400">Engagement</span><span className="text-zinc-200">{selectedNode.engagement.toLocaleString()}</span></>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default PropagationGraph;
